"use client";

import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { createContext, PropsWithChildren, useCallback, useContext, useRef } from "react";

type SubscriptionKey = readonly unknown[];
type UnsubscribeFn = () => void;

type RegisterSubscriptionFn = (
  subscriptionKey: SubscriptionKey,
  subscribe: (queryClient: QueryClient) => UnsubscribeFn,
) => UnsubscribeFn;

const SubscriptionContext = createContext<RegisterSubscriptionFn | undefined>(undefined);

export function SubscriptionProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const subscriptionMapRef = useRef<Map<string, Subscription>>(new Map());

  const registerSubscription = useCallback(
    (subscriptionKey: SubscriptionKey, subscribe: (queryClient: QueryClient) => UnsubscribeFn) => {
      const key = JSON.stringify(subscriptionKey);
      const subscriptionMap = subscriptionMapRef.current;
      const existingSubscription = subscriptionMap.get(key);
      if (existingSubscription) {
        existingSubscription.register();
        return () => existingSubscription.unregister();
      } else {
        const unsubscribe = subscribe(queryClient);
        const onUnsubscribe = () => {
          unsubscribe();
          subscriptionMap.delete(key);
        };
        const subscription = new Subscription(onUnsubscribe);
        subscriptionMap.set(key, subscription);
        return () => subscription.unregister();
      }
    },
    [queryClient],
  );

  return (
    <SubscriptionContext.Provider value={registerSubscription}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useRegisterSubscription() {
  const registerSubscription = useContext(SubscriptionContext);
  if (!registerSubscription) {
    throw new Error("useRegisterSubscription must be used within a SubscriptionProvider");
  }
  return registerSubscription;
}

class Subscription {
  #registrationsCount: number;
  #unsubscribe: UnsubscribeFn;

  constructor(unsubscribe: UnsubscribeFn) {
    this.#registrationsCount = 1;
    this.#unsubscribe = unsubscribe;
  }

  register() {
    this.#registrationsCount++;
  }

  unregister() {
    this.#registrationsCount--;
    if (this.#registrationsCount === 0) {
      this.#unsubscribe();
    }
  }
}
